<template>
  <div class="bg-grid">
    <PageTitleVue>
      Estatisticas

      <template #b-action>
        <div class="w-full">
          <Titles
            class="uppercase font-semibold"
            :w25="true"
            :left="false"
            :label="date"
          />
        </div>
      </template>
    </PageTitleVue>

    <!-- date filter -->
    <div class="calendar-container">
      <div class="calendar-header">
        <button @click="prevMonth" class="nav-button ml-2">
          <ChevronDownIcon class="h-7 w-7 text-gray-400 rotate-90" />
        </button>
        <span class="month-year uppercase">{{ monthYear }}</span>
        <button @click="nextMonth" class="nav-button mr-2">
          <ChevronDownIcon class="h-7 w-7 text-gray-400 -rotate-90" />
        </button>
      </div>
    </div>

    <!-- cards -->
    <div
      class="w-full mb-1 -mt-3"
      :class="[
        isMobile ? 'block font-medium' : 'flex justify-center font-semibold',
      ]"
    >
      <!-- entrada -->
      <TotalAmount
        :label="'total arrecadado'"
        :value="formatPrice(dataCard?.total_arrecadado)"
        :totalAmount="true"
        :current-mount="monthYear"
      />

      <!-- comissões -->
      <TotalAmount
        :label="'total de comissão'"
        :value="formatPrice(dataCard?.total_comissao)"
        :totalCosts="true"
        :current-mount="monthYear"
      />

      <!-- eventos -->
      <TotalAmount
        :label="'total de eventos'"
        :value="dataCard?.total_eventos"
        :totalServices="true"
        :current-mount="monthYear"
      />

      <!-- lutadores -->
      <TotalAmount
        :label="'lutadores cadastrados'"
        :value="dataCard?.total_lutadores"
        :totalServices="false"
      />
    </div>

    <!-- chart -->
    <div class="m-5" v-if="servicesByMonth.length == 0">
      Carregando gráfico…
    </div>

    <div v-else class="p-1 rounded-2xl" :class="[isMobile ? 'block' : 'flex']">
      <TheServicesChart
        :key="JSON.stringify(servicesByMonth)"
        :labels="Object.keys(formatAndSortData(servicesByMonth))"
        :dataset="{
          label: 'Total Arrecadado',
          data: Object.values(formatAndSortData(servicesByMonth)),
          borderColor: '#42A5F5',
          backgroundColor: 'rgba(66, 165, 245, 0.2)'
        }"
      />
    </div>

    <!-- purchase list -->
    <section>
      <h2 class="text-center text-xl font-semibold mb-4 uppercase">Vendas PPV</h2>
      <div
        class="relative overflow-x-auto shadow-md sm:rounded-lg max-h-[35vh] md:max-h-[45vh] p-2"
      >
        <DataTable :data="dataPpv">
          <template #head>
            <th scope="col" class="px-6 py-3">Lutador</th>
            <th scope="col" class="px-6 py-3">Ingressos Vendidos</th>
            <th scope="col" class="px-6 py-3">Totais</th>
            <th scope="col" class="px-6 py-3">Cache</th>
            <!-- <th scope="col" class="px-6 py-3 text-center">#</th> -->
          </template>
        </DataTable>
        <small class="text-gray-400"> resumo de {{ monthYear }} </small>
        <div
          class="sticky -bottom-2 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none z-20"
        ></div>
      </div>
    </section>
  </div>
</template>

<script setup>
import PageTitleVue from '@/components/Titles/PageTitle.vue'
import { ref, watch, computed, onBeforeMount } from 'vue'
import { storeApp } from '@/store'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { dayNames, monthNames, monthsFilter } from './lookups/days'
import TotalAmount from './components/TotalAmount.vue'
import TheServicesChart from './components/TheServicesChart.vue'
import DataTable from './components/DataTable.vue'
import Titles from './components/Titles.vue'
import homeService from './services/home.service'
import { showToast as toast } from '@/helpers/UseToasts'

// default
const defaultImg = ref(require('@/assets/logo/protege-old.png'))
const isMobile = computed(() => window.innerWidth < 768)

// main datas
const dataCard = ref(null)
const servicesByMonth = ref([])
const dataPpv = ref([])

// datas
const today = new Date()
const currentDate = ref(new Date())
const date = `${dayNames[today.getDay()]}, ${today.getDate()} de ${
  monthNames[today.getMonth()]
} de ${today.getFullYear()}`

const monthYear = computed(() => {
  const month = currentDate.value.getMonth()
  const year = currentDate.value.getFullYear()
  return `${monthsFilter[month]} ${year}`
})

function formatPrice (value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value)
}

function formatAndSortData (rawObj) {
  const mesesNumeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  const nomesMeses = {
    1: 'Jan',
    2: 'Fev',
    3: 'Mar',
    4: 'Abr',
    5: 'Mai',
    6: 'Jun',
    7: 'Jul',
    8: 'Ago',
    9: 'Set',
    10: 'Out',
    11: 'Nov',
    12: 'Dez'
  }

  const resultado = {}

  mesesNumeros.forEach(m => {
    const valor = rawObj[m] !== undefined ? rawObj[m] : 1
    resultado[nomesMeses[m]] = Number(valor.toFixed(2))
  })

  return resultado
}

async function prevMonth () {
  const month = currentDate.value.getMonth()

  if (month === 0) {
    currentDate.value = new Date(currentDate.value.getFullYear() - 1, 11, 1)
  } else {
    currentDate.value = new Date(currentDate.value.getFullYear(), month - 1, 1)
  }

  Promise.allSettled([
    await getDataCard(
      currentDate.value.getMonth() + 1,
      currentDate.value.getFullYear()
    ),
    await getPpvList(
      currentDate.value.getMonth() + 1,
      currentDate.value.getFullYear()
    )

  ])
}

async function nextMonth () {
  const month = currentDate.value.getMonth()

  if (month === 11) {
    currentDate.value = new Date(currentDate.value.getFullYear() + 1, 0, 1)
  } else {
    currentDate.value = new Date(currentDate.value.getFullYear(), month + 1, 1)
  }

  Promise.allSettled([
    await getDataCard(
      currentDate.value.getMonth() + 1,
      currentDate.value.getFullYear()
    ),
    await getPpvList(
      currentDate.value.getMonth() + 1,
      currentDate.value.getFullYear()
    )
  ])
}

async function getDataChart () {
  const year = currentDate.value.getFullYear()

  await homeService.getDataChart(year)
    .then(res => {
      servicesByMonth.value = res.data
    }).catch(err => {
      if (err?.response?.data?.error && err.response.data.status === 400) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 401) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 404) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      console.log(err)
      return toast(
        'Aviso',
        'Algo deu errado',
        'error'
      )
    })
}

async function getDataCard (month, year) {
  await homeService.getDataCard(month, year)
    .then(res => {
      dataCard.value = res.data
    }).catch(err => {
      if (err?.response?.data?.error && err.response.data.status === 400) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 401) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 404) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      console.log(err)
      return toast(
        'Aviso',
        'Algo deu errado',
        'error'
      )
    })
}

async function getPpvList (month, year) {
  await homeService.getPpvList(month, year)
    .then(res => {
      console.log(dataPpv.value)
      dataPpv.value = res.data
    }).catch(err => {
      if (err?.response?.data?.error && err.response.data.status === 400) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 401) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      if (err?.response?.data?.error && err.response.data.status === 404) {
        return toast(
          'Aviso',
          err.response.data.message,
          'error'
        )
      }

      console.log(err)
      return toast(
        'Aviso',
        'Algo deu errado',
        'error'
      )
    })
}

// company logo
watch(
  () => localStorage.getItem('__company_logo'),
  (newVal) => {
    if (newVal == null || newVal == undefined) {
      defaultImg.value = require('@/assets/logo/protege-old.png')
    } else {
      storeApp.dispatch('setCompanyLogo', {
        key: '__company_logo',
        value: newVal
      })
      defaultImg.value =
        process.env.VUE_APP_IMAGE_SERVE.concat('storage/') + newVal
    }
  },
  { immediate: true }
)

onBeforeMount(async () => {
  Promise.all([
    await getDataCard(),
    await getDataChart(),
    await getPpvList()
  ])
})
</script>

<style lang="scss" scoped>
/* From Uiverse.io by kandalgaonkarshubham */
.bg-grid {
  width: 100%;
  height: 100%;
  --s: 27px; /* control the size */
  --c: #0000, #28282803 0.5deg 119.5deg, #0000 120deg;
  --g1: conic-gradient(from 60deg at 56.25% calc(425% / 6), var(--c));
  --g2: conic-gradient(from 180deg at 43.75% calc(425% / 6), var(--c));
  --g3: conic-gradient(from -60deg at 50% calc(175% / 12), var(--c));
  background: var(--g1), var(--g1) var(--s) calc(1.73 * var(--s)), var(--g2),
    var(--g2) var(--s) calc(1.73 * var(--s)), var(--g3) var(--s) 0,
    var(--g3) 0 calc(1.73 * var(--s)) #1e1e1e00;
  background-size: calc(2 * var(--s)) calc(3.46 * var(--s));
}

.calendar-container {
  margin: 10px auto;
  padding-bottom: -10px;
  border-radius: 8px;
  width: 100%;
  max-width: 320px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: bold;
}
</style>
